import { Link } from 'react-router';
import { buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';

/** Placeholders only: each feature phase replaces its own. F0 ships no feature behaviour. */
export function Placeholder({
  title,
  note = 'This screen is built in a later phase.',
}: {
  title: string;
  note?: string;
}) {
  return (
    <section aria-labelledby="ph-title" className="flex flex-col gap-2">
      <h1 id="ph-title" className="text-xl font-semibold text-ink">
        {title}
      </h1>
      <p className="text-sm text-muted">{note}</p>
    </section>
  );
}

/**
 * A wrong address outside every shell. The same brand bar as the landing page, so the person knows where
 * they are, and the two places they can go from here: the public site or the staff sign-in.
 */
export function NotFound() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center justify-between gap-3 px-4 md:px-6">
          <span className="flex items-center gap-2 text-base font-bold text-ink">
            <span
              aria-hidden="true"
              className="grid size-7 place-items-center rounded-control bg-primary text-sm text-white"
            >
              S
            </span>
            ServeFlow
          </span>
          <Link to="/login" className={buttonClasses({ variant: 'tertiary' })}>
            Staff sign in
          </Link>
        </div>
      </header>
      <main id="main" tabIndex={-1} className="flex flex-1 flex-col justify-center px-4 py-10">
        <EmptyState
          icon="pin"
          title="Page not found"
          message="That page doesn't exist. Guests order by scanning the QR code at their table or seat."
          action={
            <Link to="/" className={buttonClasses({ variant: 'primary' })}>
              Go to ServeFlow
            </Link>
          }
        />
      </main>
    </div>
  );
}

/** A wrong address inside a shell: the shell stays, and the one way back is that screen's home. */
export function ShellNotFound({ to, label }: { to: string; label: string }) {
  return (
    <EmptyState
      icon="pin"
      title="Page not found"
      message="That page doesn't exist."
      action={
        <Link to={to} className={buttonClasses({ variant: 'secondary' })}>
          {label}
        </Link>
      }
    />
  );
}
